// src/pages/shipping/ShippingView.tsx
//
// Two pages, one file:
//   <ShippingDashboard />  — list / filter / search and the "Run
//                            end-to-end demo" trigger.
//   <ShipmentDetail />     — per-shipment audit trail, timeline,
//                            GLS card, manual "advance" controls
//                            so a reviewer can play the demo state
//                            by state.
//
// Visual style: Linear-style timeline rail; BENCHMARK teal +
//                            red logo accent for status pills; GLS
//                            block stands out with the dashed
//                            carrier rail.

import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Truck,
  Search,
  Play,
  RotateCcw,
  Check,
  ChevronRight,
  Package,
  Sparkles,
  ChevronLeft,
  User,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { EmptyState } from "@/components/ui/EmptyState";
import { cn } from "@/lib/cn";
import { formatPrice } from "@/lib/formatPrice";
import { DEMO_PRODUCTS } from "@/lib/demoData";
import { useCustomers } from "@/lib/customers/useCustomers";
import { useTranslation } from "react-i18next";
import {
  useShipping,
  STATUS_LABEL,
  STATUS_ORDER,
  type Shipment,
  type ShipmentStatus,
  type ShipmentTimelineEntry,
  type DemoSpawnInput,
} from "@/lib/shipping/shippingStore";
import {
  KnowledgeTipCard,
} from "@/components/feature/ShippingTips";

// ---------- shared helpers ----------

function statusOrderIndex(s: ShipmentStatus): number {
  return STATUS_ORDER.indexOf(s);
}

const STATUS_TONE: Record<ShipmentStatus, string> = {
  customer_order_submitted: "bg-bg-elevated text-fg-secondary",
  po_sent: "bg-accent-info/15 text-accent-info",
  pfi_received: "bg-accent-info/15 text-accent-info",
  ready_to_pay: "bg-benchmark-teal/20 text-benchmark-teal-bright",
  payment_proof_captured: "bg-benchmark-teal/15 text-benchmark-teal-bright",
  vendor_tracking_received: "bg-accent-indigo/15 text-accent-indigo",
  ready_to_dispatch: "bg-benchmark-teal/15 text-benchmark-teal-bright",
  gls_label_created: "bg-accent-indigo/15 text-accent-indigo",
  in_transit: "bg-accent-indigo/15 text-accent-indigo",
  out_for_delivery: "bg-accent-amber/15 text-accent-amber",
  delivered: "bg-accent-green/15 text-accent-green",
  reconciled: "bg-accent-green/20 text-accent-green",
};

function StatusPill({ status }: { status: ShipmentStatus }) {
  const { t } = useTranslation();
  const cls = STATUS_TONE[status];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-[11px] font-510 uppercase tracking-wider",
        cls,
      )}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {t(`admin.shipping.statusLabels.${status}`, STATUS_LABEL[status])}
    </span>
  );
}

function ProgressRail({ status }: { status: ShipmentStatus }) {
  const { t } = useTranslation();
  const idx = statusOrderIndex(status);
  return (
    <div className="flex items-center gap-1">
      {STATUS_ORDER.map((s, i) => {
        const reached = i <= idx;
        const isCurrent = i === idx;
        return (
          <div
            key={s}
            className={cn(
              "h-1 flex-1 rounded-full transition-colors",
              isCurrent
                ? "bg-benchmark-teal-bright"
                : reached
                  ? "bg-benchmark-teal/40"
                  : "bg-bg-elevated",
            )}
            title={t(`admin.shipping.statusLabels.${s}`, STATUS_LABEL[s])}
          />
        );
      })}
    </div>
  );
}

// ============================================================================
// Dashboard
// ============================================================================

export function ShippingDashboard() {
const { t } = useTranslation();
  const { shipments, spawnDemo, reset } = useShipping();
  const { customers } = useCustomers();
  const portalCustomers = customers;
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [showRunPanel, setShowRunPanel] = useState(false);

  // Prefill the e2e demo panel.
  const seedCustomer = portalCustomers[0] ?? customers[0];
  const [demoCustomerId, setDemoCustomerId] = useState<string>(seedCustomer?.id ?? "");
  const [demoProductSku, setDemoProductSku] = useState<string>(DEMO_PRODUCTS[0]?.sku ?? "");

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return shipments.filter((s) => {
      if (!q) return true;
      return (
        s.id.toLowerCase().includes(q) ||
        s.poId.toLowerCase().includes(q) ||
        s.customerName.toLowerCase().includes(q) ||
        s.vendor.toLowerCase().includes(q) ||
        s.referenceToken.toLowerCase().includes(q) ||
        (s.glsTrackingNumber ?? "").toLowerCase().includes(q)
      );
    });
  }, [shipments, query]);

  const liveCount = shipments.filter(
    (s) => s.status !== "delivered" && s.status !== "reconciled",
  ).length;
  const doneCount = shipments.filter((s) => s.status === "reconciled").length;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Truck
              className="h-5 w-5 text-benchmark-teal-bright"
              strokeWidth={1.5}
            />
            <h1 className="text-2xl font-510 tracking-tight text-fg-primary">
              {t("admin.shipping.deskTitle")}
            </h1>
          </div>
          <p className="mt-1 text-sm text-fg-tertiary">
            {t("admin.shipping.summary", {
              count: shipments.length,
              live: liveCount,
              done: doneCount,
            })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" onClick={reset}>
            <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.5} />
            {t("admin.shipping.resetToSeed")}
          </Button>
          <Button onClick={() => setShowRunPanel((s) => !s)}>
            <Play className="h-4 w-4" strokeWidth={1.5} />
            {showRunPanel ? t("common.cancel") : t("admin.shipping.runDemo")}
          </Button>
        </div>
      </header>

      {showRunPanel && (
        <Card>
          <CardContent className="space-y-4 p-5">
            <div className="flex items-center gap-2">
              <Sparkles
                className="h-4 w-4 text-accent-indigo"
                strokeWidth={1.5}
              />
              <h2 className="text-sm font-510 text-fg-primary">
                {t("admin.shipping.endToEndDemo")}
              </h2>
            </div>
            <p className="text-xs text-fg-tertiary">{t("admin.shipping.runPanelDescription")}</p>
            <div className="grid gap-3 sm:grid-cols-3">
              <RunField label={t("admin.shipping.fields.customer")}>
                <select
                  value={demoCustomerId}
                  onChange={(e) => setDemoCustomerId(e.target.value)}
                  className="h-9 w-full rounded border border-border bg-bg-surface px-3 text-sm text-fg-primary focus:border-benchmark-teal/60 focus:outline-none"
                >
                  {portalCustomers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.companyName} ({c.country})
                    </option>
                  ))}
                  {portalCustomers.length === 0 && (
                    <option value="">{t("admin.shipping.noDemoCustomer")}</option>
                  )}
                </select>
              </RunField>
              <RunField label={t("admin.shipping.fields.product")}>
                <select
                  value={demoProductSku}
                  onChange={(e) => setDemoProductSku(e.target.value)}
                  className="h-9 w-full rounded border border-border bg-bg-surface px-3 text-sm text-fg-primary focus:border-benchmark-teal/60 focus:outline-none"
                >
                  {DEMO_PRODUCTS.map((p) => (
                    <option key={p.sku} value={p.sku}>
                      {p.name} — {formatPrice(p.priceEUR, "EUR")}
                    </option>
                  ))}
                </select>
              </RunField>
              <div className="flex items-end">
                <Button
                  className="w-full"
                  onClick={() => {
                    const customer = portalCustomers.find(
                      (c) => c.id === demoCustomerId,
                    );
                    const product = DEMO_PRODUCTS.find(
                      (p) => p.sku === demoProductSku,
                    );
                    if (!customer || !product) return;
                    const input: DemoSpawnInput = {
                      customerId: customer.id,
                      customerName: customer.companyName,
                      shipTo: customer.shipTo,
                      sku: product.sku,
                      productName: product.name,
                      unitPriceEUR: product.priceEUR,
                      qty: 1,
                    };
                    const created = spawnDemo(input);
                    navigate(`/admin/shipping/${created.id}`);
                  }}
                >
                  <Play className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {t("admin.shipping.runDemo")}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Toolbar */}
      <Card>
        <CardContent className="p-3">
          <div className="relative">
            <Search
              className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-tertiary"
              strokeWidth={1.5}
            />
            <Input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("admin.shipping.searchPlaceholder")}
              className="pl-8"
              aria-label={t("admin.shipping.searchAria")}
            />
          </div>
        </CardContent>
      </Card>

      {/* List */}
      {shipments.length === 0 ? (
        <EmptyState
          icon={
            <Truck
              className="h-5 w-5 text-accent-indigo"
              strokeWidth={1.5}
            />
          }
          title={t("admin.shipping.emptyTitle")}
          description={t("admin.shipping.emptyDescription")}
        />
      ) : visible.length === 0 ? (
        <EmptyState
          title={t("admin.shipping.noMatch")}
          description={t("admin.shipping.noMatchHint")}
        />
      ) : (
        <div className="space-y-2.5">
          {visible.map((s) => (
            <ShipmentRow key={s.id} s={s} />
          ))}
        </div>
      )}

      {/* Tip card for the curious reviewer */}
      <KnowledgeTipCard />
    </div>
  );
}

function RunField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[10px] font-510 uppercase tracking-wider text-fg-tertiary">
        {label}
      </span>
      {children}
    </label>
  );
}

function ShipmentRow({ s }: { s: Shipment }) {
const { t } = useTranslation();
  const last = s.timeline[s.timeline.length - 1];
  return (
    <Link
      to={`/admin/shipping/${s.id}`}
      className="group block rounded-lg border border-border bg-bg-panel transition-colors hover:border-benchmark-teal/40"
    >
      <div className="grid gap-3 p-4 sm:grid-cols-[1.4fr_2fr_1fr_auto] sm:items-center">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] text-fg-quaternary">
              {s.id}
            </span>
            <span className="font-mono text-[10px] text-fg-quaternary">
              · {s.poId}
            </span>
          </div>
          <div className="mt-0.5 truncate text-sm font-510 text-fg-primary">
            {s.customerName}
          </div>
          <div className="truncate text-[11px] text-fg-tertiary">
            {s.lines.map((l) => `${l.qty}× ${l.name}`).join(", ")}
          </div>
        </div>
        <div className="space-y-1.5">
          <ProgressRail status={s.status} />
          <div className="flex items-center gap-2 text-[11px] text-fg-tertiary">
            <Package className="h-3 w-3" strokeWidth={1.5} />
            <span className="truncate">{s.vendor}</span>
            {s.vendorTrackingNumber && (
              <span className="font-mono text-fg-quaternary">
                · {s.vendorTrackingNumber}
              </span>
            )}
          </div>
        </div>
        <div className="space-y-1.5">
          <StatusPill status={s.status} />
          {s.glsTrackingNumber && (
            <div className="flex items-center gap-1.5 text-[11px] text-fg-tertiary">
              <Truck className="h-3 w-3 text-accent-indigo" strokeWidth={1.5} />
              <span className="font-mono text-fg-secondary">
                {s.glsTrackingNumber}
              </span>
            </div>
          )}
        </div>
        <div className="hidden text-right text-[11px] text-fg-quaternary sm:block">
          {last && <div>{new Date(last.ts).toLocaleString()}</div>}
          <div className="mt-1 flex items-center justify-end gap-1 text-fg-tertiary opacity-0 transition-opacity group-hover:opacity-100">
            {t("admin.shipping.openAction")}
            <ChevronRight className="h-3 w-3" strokeWidth={1.5} />
          </div>
        </div>
      </div>
    </Link>
  );
}

// ============================================================================
// Detail
// ============================================================================

export function ShipmentDetail() {
const { t } = useTranslation();
  const { shipmentId } = useParams<{ shipmentId: string }>();
  const { getById } = useShipping();
  const s = shipmentId ? getById(shipmentId) : undefined;

  if (!s) {
    return (
      <div className="space-y-4">
        <Link
          to="/admin/shipping"
          className="inline-flex items-center gap-1 text-sm text-fg-tertiary hover:text-fg-primary"
        >
          <ChevronLeft className="h-3 w-3" strokeWidth={1.5} />
          {t("admin.shipping.backToDesk")}
        </Link>
        <EmptyState
          title={t("admin.shipping.notFound")}
          description={t("admin.shipping.notFoundDescription")}
          icon={<Truck className="h-5 w-5 text-fg-quaternary" strokeWidth={1.5} />}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Link
        to="/admin/shipping"
        className="inline-flex items-center gap-1 text-sm text-fg-tertiary hover:text-fg-primary"
      >
        <ChevronLeft className="h-3 w-3" strokeWidth={1.5} />
        {t("admin.shipping.backToDesk")}
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-fg-quaternary">
              {s.id} · {s.poId}
            </span>
          </div>
          <h1 className="mt-0.5 text-2xl font-510 tracking-tight text-fg-primary">
            {s.customerName}
          </h1>
          <div className="mt-1 flex items-center gap-2 text-xs text-fg-tertiary">
            <User className="h-3 w-3" strokeWidth={1.5} />
            {s.lines.map((l) => `${l.qty}× ${l.name}`).join(", ")}
          </div>
        </div>
        <StatusPill status={s.status} />
      </header>

      {/* Top-line progress */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <ProgressRail status={s.status} />
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-fg-tertiary">
            <span>
              Vendor:{" "}
              <span className="text-fg-secondary">{s.vendor}</span>
            </span>
            {s.vendorTrackingNumber && (
              <span className="font-mono">
                Vendor tracking: {s.vendorTrackingNumber}
              </span>
            )}
            <span className="font-mono">Reference: {s.referenceToken}</span>
            <span>
              Total:{" "}
              <span className="font-mono text-fg-secondary">
                {formatPrice(s.amountCents / 100, "EUR")}
              </span>
            </span>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        {/* Timeline */}
        <Card>
          <CardContent className="p-5">
            <h2 className="mb-4 text-xs font-510 uppercase tracking-wider text-fg-tertiary">
              {t("admin.shipping.timelineTitle")}
            </h2>
            <Timeline s={s} />
          </CardContent>
        </Card>

        {/* GLS card */}
        <GlsCard s={s} />
      </div>
    </div>
  );
}

function Timeline({ s }: { s: Shipment }) {
  return (
    <ol className="space-y-3">
      {s.timeline.map((e, i) => (
        <TimelineEntry
          key={`${s.id}-${i}`}
          e={e}
          isLast={i === s.timeline.length - 1}
        />
      ))}
    </ol>
  );
}

function TimelineEntry({
  e,
  isLast,
}: {
  e: ShipmentTimelineEntry;
  isLast: boolean;
}) {
  return (
    <li className="flex gap-3">
      <div className="flex flex-col items-center">
        <div
          className={cn(
            "grid h-6 w-6 flex-shrink-0 place-items-center rounded-full ring-2",
            e.actor === "human"
              ? "bg-benchmark-teal/20 text-benchmark-teal-bright ring-benchmark-teal/40"
              : e.actor === "agent"
                ? "bg-accent-indigo/20 text-accent-indigo ring-accent-indigo/40"
                : "bg-bg-elevated text-fg-tertiary ring-border",
          )}
        >
          {e.actor === "human" ? (
            <User className="h-3 w-3" strokeWidth={2} />
          ) : e.actor === "agent" ? (
            <Sparkles className="h-3 w-3" strokeWidth={2} />
          ) : (
            <span className="h-1.5 w-1.5 rounded-full bg-current" />
          )}
        </div>
        {!isLast && (
          <div className="my-1 w-px flex-1 bg-border" />
        )}
      </div>
      <div className="-mt-0.5 pb-2">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-510 text-fg-primary">
            {STATUS_LABEL[e.status]}
          </span>
          <span className="font-mono text-[10px] text-fg-quaternary">
            {new Date(e.ts).toLocaleString()}
          </span>
        </div>
        <p className="mt-0.5 text-xs text-fg-tertiary">{e.note}</p>
      </div>
    </li>
  );
}

function GlsCard({ s }: { s: Shipment }) {
const { t } = useTranslation();
  const hasLabel = !!s.glsTrackingNumber;
  return (
    <Card>
      <CardContent className="space-y-4 p-5">
        <div className="flex items-center gap-2">
          <Truck
            className="h-4 w-4 text-accent-indigo"
            strokeWidth={1.5}
          />
          <h2 className="text-xs font-510 uppercase tracking-wider text-fg-tertiary">
            {t("admin.shipping.glsDispatch")}
          </h2>
        </div>
        <div className="space-y-1 text-xs text-fg-tertiary">
          <div className="text-fg-quaternary">{t("admin.shipping.shippingTo")}</div>
          <div className="space-y-0.5 text-fg-secondary">
            {s.shipTo.map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        </div>
        <div className="space-y-2 rounded-md border border-border bg-bg-canvas p-3">
          {hasLabel ? (
            <>
              <Row label={t("admin.shipping.fields.carrier")}>
                <Badge variant="teal" size="sm">
                  GLS
                </Badge>
              </Row>
              <Row label={t("admin.shipping.fields.tracking")}>
                <span className="font-mono text-fg-secondary">
                  {s.glsTrackingNumber}
                </span>
              </Row>
              <Row label={t("admin.shipping.fields.service")}>
                <span className="uppercase tracking-wider text-fg-secondary">
                  {s.glsServiceLevel ?? "—"}
                </span>
              </Row>
              <div className="mt-3 grid grid-cols-3 gap-2 text-[10px] uppercase tracking-wider text-fg-quaternary">
                <Stage
                  label={t("admin.shipping.stages.labelCreated")}
                  active={statusOrderIndex(s.status) >=
                    statusOrderIndex("gls_label_created")}
                />
                <Stage
                  label={t("admin.shipping.stages.inTransit")}
                  active={
                    statusOrderIndex(s.status) >=
                    statusOrderIndex("in_transit")
                  }
                />
                <Stage
                  label={t("admin.shipping.stages.outForDelivery")}
                  active={
                    statusOrderIndex(s.status) >=
                    statusOrderIndex("out_for_delivery")
                  }
                />
                <Stage
                  label={t("admin.shipping.stages.delivered")}
                  active={
                    statusOrderIndex(s.status) >=
                    statusOrderIndex("delivered")
                  }
                  fullWidth
                />
              </div>
            </>
          ) : (
            <div className="rounded border border-dashed border-border p-3 text-center text-xs text-fg-quaternary">
              {t("admin.shipping.noLabelYet")}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2 text-xs">
      <span className="text-fg-quaternary">{label}</span>
      {children}
    </div>
  );
}

function Stage({
  label,
  active,
  fullWidth,
}: {
  label: string;
  active: boolean;
  fullWidth?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-start gap-1 rounded px-2 py-1",
        active
          ? "bg-benchmark-teal/15 text-fg-primary"
          : "bg-bg-elevated/60 text-fg-quaternary",
        fullWidth && "col-span-3",
      )}
    >
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider">
        {active ? (
          <Check className="h-3 w-3 text-accent-green" strokeWidth={2} />
        ) : (
          <span className="h-3 w-3 rounded-full border border-border" />
        )}
        {label}
      </div>
    </div>
  );
}
